import React, { useRef } from "react";
import { Box, Chip, Grid, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import more from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import SchedulePanel from "./SchedulePanel";
import { enterpriseTokens, overviewChartPalette } from "styles/theme";

if (typeof more === "function") {
    more(Highcharts);
}
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const SchedulePerformanceIndex  = ({kpiData}) => {

    const chartRef = useRef(null);
    const row = kpiData?.data?.[0];
   // const spi=((row?.actualProgress/row?.planProgress)*100).toFixed(2);
    const plan = Number(row?.planProgress) || 0;
    const actual = Number(row?.actualProgress) || 0;

    // SPI is a ratio: EV / PV
    const spiRaw = plan > 0 ? actual / plan : 0;

    // Your gauge is 0..2, so clamp SPI to that range (optional)
    const spi = clamp(spiRaw, 0, 2);
    const actualColor = spi >= 1 ? overviewChartPalette.teal : spi >= 0.5 ? overviewChartPalette.amber : overviewChartPalette.coral;
    const statusLabel = spi >= 1 ? "Healthy" : spi >= 0.5 ? "Watch" : "At risk";

    //const clampedValue = progress == 0 ? Math.min(speed, costKpi?.data?.[0]?.cpi) : Math.min(speed, 1);
    //const clampedValue = progress==0?Math.min(speed, spi):Math.min(speed, 1);
    const clampedValue = clamp(spiRaw, 0, 2);

    const chartOptions = {
        chart: {
            type: "gauge",
            plotBackgroundColor: null,
            plotBackgroundImage: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: 210,
            backgroundColor: "transparent",
            spacingLeft: 12,
            spacingRight: 12,
            spacingTop: 4,
            spacingBottom: 0,
        },
        title: {
            text: "",
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ["50%", "95%"],
            size: "145%",
        },
        yAxis: {
            min: 0,
            max: 2,
            tickPixelInterval: 72,
            tickPosition: "inside",
            tickColor: Highcharts.defaultOptions.chart.backgroundColor || "#FFFFFF",
            tickLength: 20,
            tickWidth: 0,
            minorTickInterval: null,
            labels: {
                enabled: false,
                // distance: 20,
                style: {
                    fontSize: "12px",
                },
            },
            lineWidth: 0,
            plotBands: [
                {
                    from: 0,
                    to: 0.5,
                    color: overviewChartPalette.coral,
                    thickness: 34,
                    borderRadius: '3px'
                },
                {
                    from: 0.5,
                    to: 1,
                    color: overviewChartPalette.amber,
                    thickness: 34,
                    borderRadius: '3px'
                },
                {
                    from: 1,
                    to: 1.5,
                    color: overviewChartPalette.teal,
                    thickness: 34,
                    borderRadius: '3px'
                },
                {
                    from: 1.5,
                    to: 2,
                    color: overviewChartPalette.indigo,
                    thickness: 34,
                    borderRadius: '3px'
                }
            ]

        },
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "Cost Performance Index",
                data: [clampedValue],
                tooltip: {
                    valueSuffix: "",
                },
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return `<div>${clampedValue.toFixed(2)}</div>`;
                    },
                    borderWidth: 0,
                    y: -60,
                    // x:20,
                    style: {
                        fontSize: "30px",
                        fontWeight: "700",
                        color: enterpriseTokens.navy,
                        textOutline: "none",
                    },
                },
                dial: {
                    radius: "80%",
                    backgroundColor: overviewChartPalette.navy,
                    baseWidth: 8,
                    baseLength: "0%",
                    rearLength: "0%",
                },
                pivot: {
                    backgroundColor: overviewChartPalette.navy,
                    radius: 6,
                },
            },
        ],
    };

    return (
        
        <SchedulePanel
            eyebrow="Delivery efficiency"
            title="Schedule Health"
            subtitle="Measure delivered progress relative to the approved plan."
            action={
                <Chip
                    label={statusLabel}
                    size="small"
                    sx={{
                        bgcolor: spi >= 1 ? "#DCFCE7" : spi >= 0.5 ? "#FEF3C7" : "#FEE2E2",
                        color: actualColor,
                        borderRadius: "999px",
                        fontWeight: 600,
                    }}
                />
            }
            bodySx={{ p: 1.5, pt: 1 }}
        >
            <Grid container alignItems="center" spacing={1}>
                <Grid size={{ xs:12, md: 5 }}>
                    <Box sx={{ p: 1, display: "grid", gap: 1.25 }}>
                        <Box sx={{ p: 1.25, borderRadius: "3px", bgcolor: "#F8FAFC", border: `1px solid ${enterpriseTokens.lineSoft}` }}>
                            <Typography
                                sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    mb: 0,
                                    color: enterpriseTokens.primary,
                                }}
                            >
                                Planned Completion
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    mb: 0,
                                    color: enterpriseTokens.navy,
                                }}
                            >
                                {row?.planProgress}%
                            </Typography>
                            <LinearProgress variant="determinate" value={Math.min(plan, 100)} sx={{ mt: 0.75, height: 6, borderRadius: 99, bgcolor: "#E2E8F0", "& .MuiLinearProgress-bar": { bgcolor: overviewChartPalette.indigo } }} />
                        </Box>
                        <Box sx={{ p: 1.25, borderRadius: "3px", bgcolor: "#FFFFFF", border: `1px solid ${enterpriseTokens.lineSoft}` }}>
                            <Typography
                                sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    mb: 0,
                                    color: actualColor,
                                }}
                            >
                                Delivered Completion
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 700,
                                    fontSize: '24px',
                                    mb: 0,
                                    color: enterpriseTokens.navy,
                                }}
                            >
                                {row?.actualProgress}%
                            </Typography>
                            <LinearProgress variant="determinate" value={Math.min(actual, 100)} sx={{ mt: 0.75, height: 6, borderRadius: 99, bgcolor: "#E2E8F0", "& .MuiLinearProgress-bar": { bgcolor: actualColor } }} />
                        </Box>
                    </Box>
                </Grid>
                <Grid size={{xs:12, md: 7 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                </Grid>
            </Grid>
        </SchedulePanel>
    );
};

export default SchedulePerformanceIndex;
